// src/components/Footer.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { scroller } from 'react-scroll';
import '../styles/Footer.css';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'ok' | 'err' | null

  // <-- plain JS ref (no TypeScript generics)
  const debounceRef = useRef(null);

  // Observe scroll and show footer when near the bottom
  useEffect(() => {
    const handler = () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        const doc = document.documentElement;
        const scrolled = window.scrollY;
        const available = doc.scrollHeight - window.innerHeight;
        const ratio = available <= 0 ? 1 : scrolled / available;
        // show when user reaches ~99%
        setIsFooterVisible(ratio >= 0.99);
      }, 120);
    };

    window.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler);
    handler(); // run once

    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, []);

  const handleScrollToSection = useCallback((section) => {
    // keep same behavior: if on home route, scroll; else navigate with state
    if (location.pathname === '/') {
      scroller.scrollTo(section, {
        duration: 700,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: -80,
      });
    } else {
      navigate('/', { state: { scrollToSection: section } });
    }
  }, [location.pathname, navigate]);

  const address = "2Q5C+WJX, Addis Ababa";
  const phoneNumber = "+251115577723";
  const googleMapsUrl = `https://www.google.com/maps/place/${encodeURIComponent(address)}`;

  const formatPhoneNumber = (phone) => `tel:${phone.replace(/\s/g, '')}`;

  // local (stub) newsletter submit
  const submitNewsletter = (e) => {
    if (e) e.preventDefault();
    if (!newsletterEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      setNewsletterStatus('err');
      setTimeout(() => setNewsletterStatus(null), 1800);
      return;
    }
    // In a real app: send to backend / mailchimp etc.
    setNewsletterStatus('ok');
    setNewsletterEmail('');
    setTimeout(() => setNewsletterStatus(null), 2200);
  };

  return (
    <footer
      id="footer"
      className={`footer ${isFooterVisible ? 'show' : ''}`}
      role="contentinfo"
      aria-hidden={!isFooterVisible}
    >
      <div className="footer-inner">
        <div className="footer-column contact">
          <h3 className="footer-heading">Contact Us</h3>
          <address className="address">
            <div>Afework International Group</div>
            <div>
              <a className="map-link" href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                {address}
              </a>
            </div>
            <div>
              <a className="phone-link" href={formatPhoneNumber(phoneNumber)} aria-label={`Call ${phoneNumber}`}>
                {phoneNumber}
              </a>
            </div>
            <div className="small muted">Office hours: Mon–Fri, 09:00–17:00</div>
          </address>
        </div>

        <div className="footer-column links">
          <h3 className="footer-heading">Quick Links</h3>
          <ul className="footer-links-list" aria-label="Quick links">
            <li><button onClick={() => handleScrollToSection('video-background')} className="footer-link">Home</button></li>
            <li><button onClick={() => handleScrollToSection('about-us-section')} className="footer-link">About</button></li>
            <li><button onClick={() => handleScrollToSection('products-services-section')} className="footer-link">Products &amp; Services</button></li>
            <li><button onClick={() => handleScrollToSection('contact-section')} className="footer-link">Contact</button></li>
          </ul>
        </div>

        <div className="footer-column social">
          <h3 className="footer-heading">Stay updated</h3>

          <form className="newsletter" onSubmit={submitNewsletter}>
            <label htmlFor="footer-news" className="visually-hidden">Your email</label>
            <input
              id="footer-news"
              type="email"
              placeholder="you@email.com"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="newsletter-input"
              aria-label="Enter email to subscribe"
            />
            <button type="submit" className={`newsletter-btn ${newsletterStatus === 'ok' ? 'ok' : newsletterStatus === 'err' ? 'err' : ''}`}>
              {newsletterStatus === 'ok' ? '✓' : 'Subscribe'}
            </button>
          </form>

          <div className="legal small muted">
            <div>Afework International Group</div>
            <div>© {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
